import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Target, Eye, CheckCircle2 } from 'lucide-react';
import { fetchAbout, resolveImageUrl } from '../lib/travelApi';
import Gallery from '../components/Gallery';
import Testimonials from '../components/Testimonials';

const AboutPage = () => {
  const navigate = useNavigate();
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAbout()
      .then(setAbout)
      .catch(() => setAbout(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div data-testid="about-page" className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        {about?.hero_image && (
          <img
            src={resolveImageUrl(about.hero_image)}
            alt="About The Navkar Travels"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 text-center px-4 max-w-3xl">
          <h1 data-testid="about-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white drop-shadow-lg">
            {about?.heading || 'About Us'}
          </h1>
          <div className="w-24 h-1 bg-amber-500 mx-auto mt-6" />
        </div>
      </section>

      {/* Intro / Story */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="space-y-6">
              <span className="text-amber-600 font-semibold uppercase tracking-wider text-sm">Our Story</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Crafting Journeys, Creating Memories</h2>
              <p className="text-gray-600 text-lg leading-relaxed whitespace-pre-line">{about?.intro}</p>
            </div>
            {about?.story_image && (
              <div className="relative">
                <img
                  src={resolveImageUrl(about.story_image)}
                  alt="Our story"
                  className="rounded-2xl shadow-2xl w-full h-[420px] object-cover"
                />
                <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-amber-500 rounded-2xl -z-0 hidden md:block" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="border-t-4 border-t-amber-500 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mb-5">
                  <Target className="w-7 h-7 text-amber-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Our Mission</h3>
                <p className="text-gray-600 leading-relaxed">{about?.mission}</p>
              </CardContent>
            </Card>
            <Card className="border-t-4 border-t-gray-800 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-5">
                  <Eye className="w-7 h-7 text-gray-800" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Our Vision</h3>
                <p className="text-gray-600 leading-relaxed">{about?.vision}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Highlights */}
      {about?.highlights?.length > 0 && (
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Why Choose Us</h2>
              <div className="w-20 h-1 bg-amber-500 mx-auto mt-4" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
              {about.highlights.map((item, idx) => (
                <div
                  key={idx}
                  data-testid={`about-highlight-${idx}`}
                  className="flex items-start gap-4 bg-white border border-gray-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
                >
                  <CheckCircle2 className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
                  <span className="text-gray-700 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      {/* Gallery (merged from former Gallery page) */}
      <Gallery />

      {/* Testimonials */}
      <Testimonials />

      {/* CTA */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to start your journey?</h2>
          <p className="text-gray-300 mb-8 max-w-xl mx-auto">
            Let our team craft a personalized trip designed around your dreams and budget.
          </p>
          <Button
            onClick={() => navigate('/contact')}
            data-testid="about-cta-btn"
            size="lg"
            className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6"
          >
            Plan Your Trip
          </Button>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
